import cookie from 'react-cookie';
import _ from 'lodash';

import { parseJSON } from 'helpers/ContentHelper';

export function getCookie({ name, doNotParse = false }) {
  window.cookie = cookie;
  if (!name) return null;
  return cookie.load(name, doNotParse);
}

export function setCookie({ name, value, options = {} }) {
  if (!name || value === undefined) return null;
  cookie.save(name, value, options);
  return true;
}

export function getCookieExpires() {
  const expires = new Date();
  expires.setMonth(expires.getMonth() + 1);
  return expires;
}

export function removeCookie({ key, options }) {
  if (!key) return null;
  const defaultOptions = { path: '/' };
  const opts = options || {};
  cookie.remove(key, { ...defaultOptions, ...opts });
  return null;
}

export const setLocalStorageItem = ({ key, value = null }) => {
  if (isLocalStorageNameSupported()) {
    const localStorage = getLocalStorage();
    if (!localStorage || !key || value === null) return;
    localStorage.setItem(key, value);
    return;
  }
  if (!key || !value) return;
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  const options = {
    path: '/',
    expires,
  };
  setCookie({ name: key, value, options });
};

export const getLocalStorageItem = ({ key }) => {
  if (!key) return null;
  if (isLocalStorageNameSupported()) {
    const localStorage = getLocalStorage();
    if (!localStorage) return null;
    return localStorage.getItem(key);
  }
  return getCookie({ name: key, doNotParse: true }) || null;
};

export const removeLocalStorageItem = ({ key }) => {
  if (!key) return null;
  if (isLocalStorageNameSupported()) {
    const localStorage = getLocalStorage();
    if (!localStorage || !key) return null;
    return localStorage.removeItem(key);
  }
  return removeCookie({ name: key });
};

/* eslint-disable */
export const getLocalStorage = () => {
  try {
    return _.get(window, 'localStorage');
  } catch (error) {
    console.log('Local storage error', error);
  }
  return null;
};
/* eslint-enable */

export const setSessionStorageItem = ({ key, value }) => {
  if (!key) return null;
  if (isLocalStorageNameSupported()) {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
      return null;
    }
    return sessionStorage.setItem(key, value);
  }
  const options = {
    path: '/',
  };
  return setCookie({ name: key, value, options });
};

export const getSessionStorageItem = ({ key }) => {
  if (!key) return null;
  if (isLocalStorageNameSupported()) {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
      return null;
    }
    return sessionStorage.getItem(key);
  }
  return getCookie({ name: key, doNotParse: true }) || null;
};

const getSessionStorage = () => window?.sessionStorage;

export const setPaginatedStorage = ({ key, value, options }) => {
  const limit = options.limit || 10;

  if (!(value instanceof Array)) {
    return;
  }

  const valueCopy = value.slice();

  let remainder = valueCopy.length;
  let counter = 0;

  do {
    const slice = valueCopy.splice(0, limit);
    remainder = slice.length;

    const storageBlockName = `${key}-${counter}`;
    if (slice.length) {
      const value = JSON.stringify(slice);
      setLocalStorageItem({ key: storageBlockName, value });
    }

    counter += 1;
  } while (remainder > 0);
};

export const getPaginatedStorage = ({ key }) => {
  let result = [];

  let counter = 0;
  let data = null;
  do {
    const storageBlockName = `${key}-${counter}`;
    data = getLocalStorageItem({ key: storageBlockName });
    const parsedData = data ? parseJSON(data) : null;
    if (parsedData) result = result.concat(parsedData);
    counter += 1;
  } while (data);

  return result;
};

export const removePaginatedStorage = ({ key }) => {
  let data = null;
  let counter = 0;
  do {
    const storageBlockName = `${key}-${counter}`;
    data = getLocalStorageItem({ key: storageBlockName });
    if (data) removeLocalStorageItem({ key: storageBlockName });
    counter += 1;
  } while (data);
};

export function cookieFactory(cookieName) {
  let cookie = getCookie({ name: cookieName });
  function getValue() {
    return cookie;
  }
  function setValue({ name, value, options = {} }) {
    cookie = value;
    const key = name || cookieName;
    setCookie({ name: key, value, options });
  }
  function removeValue({ options = {} }) {
    cookie = null;
    removeCookie({ key: cookieName, options });
  }

  return {
    getValue,
    setValue,
    removeValue,
  };
}

export const getDomain = () => {
  const url = window.location.hostname;
  const urlItems = url.split('.');
  urlItems.shift();
  const domain = urlItems.join('.');
  return `.${domain}`;
};

const isLocalStorageNameSupported = () => {
  const testKey = 'testKey';
  const storage = window.localStorage;
  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};
